<template>
  <div
    v-if="transactionStatusPage === TransactionStatus.PENDING"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <img class="info-image" src="../../../public/assets/high_five.svg" alt="high_five">
      <span class="my-page-title">{{ $t('ticketsReserved') }}</span>
      <p class="description">
        {{ $t('ticketsReservedDescription.part1') }}
        <strong
          v-if="!isLoadingOrder"
        >{{
           isFreeOrder
             ? $t('ticketsReservedDescription.part2Free')
             : $t('ticketsReservedDescription.part2')
         }}
          {{ customerEmail }}.</strong>
      </p>
      <div
        class="transaction-status-container"
        :style="{ backgroundColor: transactionStatusData.backgroundColor }"
      >
        <spinner v-if="transactionStatus === TransactionStatus.PENDING || isLoadingOrder" />
        <v-icon v-else :color="transactionStatusData.color" size="40">
          {{ transactionStatusData.icon }}
        </v-icon>
        <div>
          <p
            class="transaction-status-title"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.title }}
          </p>
          <p
            class="transaction-status-description"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.description }}
          </p>
        </div>
      </div>
      <span v-if="!isLoadingOrder" class="description">
        {{
          isFreeOrder
            ? $t('ticketsReservedDescription.part3Free')
            : $t('ticketsReservedDescription.part3')
        }}
      </span>
    </div>
  </div>
  <div
    v-else-if="transactionStatusPage === TransactionStatus.FAILED"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <img class="info-image" src="/assets/tickets-error-visual.svg" alt="error">
      <span class="my-page-title error-title">{{ $t('ticketsFailedPayment') }}</span>
      <span class="description">
        {{ $t('ticketsFailedPaymentDescription.part1')
        }}<strong>{{ $t('ticketsFailedPaymentDescription.part2') }}</strong>{{ $t('ticketsFailedPaymentDescription.part3') }}
      </span>
      <main-button :href="repeatPurchaseUrl" color="black" style="width: 100%; margin-top: 3rem">
        {{ $t('repeatPurchase') }}
      </main-button>
    </div>
  </div>
  <div v-else class="registration-success-page-container">
    <div class="registration-success-page">
      <img class="info-image" src="../../../public/assets/high_five.svg" alt="high_five">
      <span class="my-page-title">{{ $t('paymentSuccessful') }}</span>
      <span class="description">
        {{ $t('ticketReady') }}
      </span>
      <div id="buttons-container">
        <a
          v-if="user && order"
          target="_blank"
          class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
          :href="`${customerURL}/orders/${order.uuid}`"
        >
          {{ $t('viewTheOrder') }}
        </a>
        <a
          id="download-ticket"
          :class="{ 'v-btn--disabled': !ticketDownloaded }"
          class="main-button v-btn v-btn--depressed v-btn--round theme--light download-ticket"
          :disabled="!ticketDownloaded"
        >
          <span>{{ $t('getMyTicket') }}</span>
          <v-progress-circular
            v-show="!ticketDownloaded && !ticketError"
            color="primary"
            indeterminate
          />
        </a>
      </div>
      <button class="button-no-background" @click="goToPublicPage">
        {{ $t('backToEventPage') }}
      </button>
    </div>
  </div>
</template>

<script>
import { getEventSettings, getOrderInfoByUUID, getPaymentStatusToken } from '../../http'
import ApplicationHeader from '../shared/application-header.vue'
import ApplicationFooter from '../shared/application-footer.vue'
import axios from 'axios'
import { GET_LOGGED_USER } from '../../graphql/user.graphql'
import { CHECK_ORDER_WS_PAY_STATUS } from '../../graphql/order.graphql'
import { customerURL, landingURL, frontendURL } from '../../utils'
import i18next from 'i18next'
import { TransactionStatus } from '../../types'

export default {
  name: 'successful-payment',
  components: {
    ApplicationHeader,
    ApplicationFooter,
  },
  data() {
    return {
      slug: undefined,
      ticketDownloaded: false,
      ticketError: false,
      customerURL,
      landingURL,
      user: null,
      order: null,
      transactionStatusPage: TransactionStatus.PENDING,
      transactionStatus: TransactionStatus.PENDING,
      pollCount: 0,
      pollTimer: null,
      loading: false, // Changed to false so we can show the pending UI
      isFreeOrder: false,
      orderFetchPollCount: 0,
      orderFetchPollTimer: null,
      orderFetchError: false,
      isLoadingOrder: true, // New flag to track if we're still fetching the order
    }
  },
  computed: {
    TransactionStatus() {
      return TransactionStatus
    },
    repeatPurchaseUrl() {
      if (this.order && this.order.event && this.order.event.slug) {
        return `${frontendURL}/events/${this.order.event.slug}/register?lang=${i18next.language}`
      }
      if (this.slug) {
        return `${frontendURL}/events/${this.slug}/register?lang=${i18next.language}`
      }

      return landingURL
    },
    transactionStatusData() {
      if (this.transactionStatus === TransactionStatus.PENDING) {
        return {
          title: this.$t('transactionStatuses.PENDING'),
          description: this.$t('transactionStatusesDescription.PENDING'),
          backgroundColor: '#E6F1F8',
          color: '#0277BD',
          icon: '',
        }
      } else if (this.transactionStatus === TransactionStatus.SUCCESSFUL) {
        return {
          title: this.$t('transactionStatuses.SUCCESSFUL'),
          description: this.$t('transactionStatusesDescription.SUCCESSFUL'),
          backgroundColor: '#EEF6EF',
          color: '#0E7E1A',
          icon: 'check_circle',
        }
      } else if (this.transactionStatus === TransactionStatus.FAILED) {
        return {
          title: this.$t('transactionStatuses.FAILED'),
          description: this.$t('transactionStatusesDescription.FAILED'),
          backgroundColor: '#FBEBED',
          color: '#BD2A3C',
          icon: 'error',
        }
      }
    },
    customerEmail() {
      if (this.order && this.order.buyer && this.order.buyer.fields) {
        return this.order.buyer.fields['email'] || this.order.buyer.fields['Email'] || ''
      }

      return ''
    },
  },
  async mounted() {
    localStorage.removeItem('order')
    await this.fetchOrderWithPolling()
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopOrderFetchPolling()
  },
  methods: {
    async fetchOrderWithPolling() {
      const uuid = this.$router.history.current.params.orderUUID

      try {
        this.order = await getOrderInfoByUUID(uuid)

        if (this.order) {
          // Successfully fetched order, stop polling and process it
          this.isLoadingOrder = false
          this.stopOrderFetchPolling()
          await this.processOrder()
        } else {
          // Order is null but no error - might be invalid UUID
          // After several retries with null response, it's probably a bad UUID
          if (this.orderFetchPollCount >= 5) {
            // If consistently null, redirect to 404
            this.$router.push('/404')
          } else {
            this.scheduleNextOrderFetch()
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error)
        this.orderFetchError = true
        this.scheduleNextOrderFetch()
      }
    },

    scheduleNextOrderFetch() {
      this.orderFetchPollCount++

      let interval
      if (this.orderFetchPollCount < 10) {
        // First 10 calls: every 2 seconds
        interval = 2000
      } else if (this.orderFetchPollCount < 20) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.orderFetchPollCount < 30) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // After 30 attempts: every 30 seconds indefinitely
        interval = 30000
      }

      this.orderFetchPollTimer = setTimeout(() => {
        this.fetchOrderWithPolling()
      }, interval)
    },

    stopOrderFetchPolling() {
      if (this.orderFetchPollTimer) {
        clearTimeout(this.orderFetchPollTimer)
        this.orderFetchPollTimer = null
      }
    },

    async processOrder() {
      this.isFreeOrder =
        this.order.paymentType === 'FREE' || ['EUR 0', 'EUR 0.00'].includes(this.order.price)

      if (this.isFreeOrder) {
        this.transactionStatus = TransactionStatus.SUCCESSFUL
        this.transactionStatusPage = TransactionStatus.SUCCESSFUL

        this.$nextTick(async () => {
          await this.downloadTicket(this.order.uuid)
        })
      }

      const preferredLanguage = this.order.event.organization.preferredLanguage
      i18next.changeLanguage(preferredLanguage == 'en' ? 'en' : 'hr')

      const { slug } = await getEventSettings(this.order.eventId)
      this.slug = slug
      const { data = {} } = await this.getUser()
      const { me: user } = data || {}
      this.user = user

      // Start polling for WsPay status if transaction is pending
      if (!this.isFreeOrder && this.transactionStatusPage === TransactionStatus.PENDING) {
        this.startPolling()
      }
    },

    goToPublicPage() {
      window.open(`${landingURL}/events/${this.slug}`, '_self')
    },
    async downloadTicket(uuid) {
      try {
        const { data: ticket } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/tickets/${uuid}`,
        )
        const byteString = window.atob(ticket.body)
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const int8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([int8Array], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const downloadLink = document.getElementById('download-ticket')
        downloadLink.href = url
        downloadLink.setAttribute('download', 'CoreEvent-karte.pdf')
        this.ticketDownloaded = true
      } catch (e) {
        this.ticketError = true
      }
    },
    async getUser() {
      return this.$apollo.query({
        query: GET_LOGGED_USER,
      })
    },
    async checkWsPayStatus() {
      try {
        // Prepare the order items for the query
        const items = this.order.purchases.map(purchase => ({
          orderItemId: purchase.orderItemId,
          count: 1,
        }))

        // Group items by orderItemId to get the count
        const groupedItems = items.reduce((acc, item) => {
          const existing = acc.find(i => i.orderItemId === item.orderItemId)
          if (existing) {
            existing.count += 1
          } else {
            acc.push({ ...item })
          }

          return acc
        }, [])

        const { data } = await this.$apollo.query({
          query: CHECK_ORDER_WS_PAY_STATUS,
          variables: {
            order: {
              uuid: this.order.uuid,
              orderId: this.order.id,
              statusToken: getPaymentStatusToken(this.order.uuid),
              eventId: this.order.eventId,
              items: groupedItems,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (data && data.checkOrderWsPayStatus && data.checkOrderWsPayStatus.success) {
          // Payment successful
          this.transactionStatus = TransactionStatus.SUCCESSFUL
          setTimeout(async () => {
            this.transactionStatusPage = TransactionStatus.SUCCESSFUL
            await this.downloadTicket(this.order.uuid)
          }, 2000)
          this.stopPolling()
        } else {
          // Continue polling
          this.scheduleNextPoll()
        }
      } catch (error) {
        console.error('Error checking WsPay status:', error)
        this.scheduleNextPoll()
      }
    },
    scheduleNextPoll() {
      this.pollCount++

      let interval
      if (this.pollCount < 20) {
        // First 20 calls: every 3 seconds
        interval = 3000
      } else if (this.pollCount < 30) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.pollCount < 40) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // A timeout is not proof of payment failure. Keep reconciling slowly
        // until the backend confirms a terminal state or this page closes.
        interval = 30000
      }

      this.pollTimer = setTimeout(() => {
        this.checkWsPayStatus()
      }, interval)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    startPolling() {
      // Start polling immediately
      this.pollCount = 0
      this.checkWsPayStatus()
    },
  },
}
</script>

<style scoped>
.button-no-background {
  font-family: Raleway;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.mt-50 {
  margin-top: 50px;
}

.my-page-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  font-size: 38px;
  line-height: 45px;
  color: #ff4c00;
  margin-bottom: 24px;
  margin-top: 60px;
}

.error-title {
  color: #bd2a3c !important;
}

.registration-success-page-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.registration-success-page {
  max-width: 40rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.info-image {
  width: 100%;
  height: 30rem;
}

.description {
  font-family: Source Sans Pro, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 23px;
  text-align: center;
  color: #333333;
}

.mt-220 {
  margin-top: 220px;
}

#buttons-container {
  margin: 2rem 0;
  max-width: 640px !important;
  display: flex;
}

@media screen and (max-width: 561px) {
  #buttons-container {
    flex-direction: column;
  }
}

.download-ticket {
  background-color: white;
  border: 2px solid #0277bd;
  color: #0277bd;
}

.v-btn--disabled {
  border: none;
}

#download-ticket .v-progress-circular {
  margin-left: 10px;
}

.main-button {
  font-size: 16px;
  height: 36px;
  font-family: Raleway, sans-serif;
  font-weight: bold;
  line-height: 1.5;
}

.transaction-status-container {
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 0.75rem;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  border-radius: 62.4375rem;
  margin: 1.5rem 0;
}

.transaction-status-title {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 110%;
  text-align: left;
}

.transaction-status-description {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  text-align: left;
}
</style>
